import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApprovalPolicy,
  documentTypeSchema,
  type DocumentTypeInput,
} from '@qualicloud/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  AlertDialog,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  FormError,
  IconAlertTriangle,
  IconChevronDown,
  IconChevronUp,
  IconFolder,
  IconGrip,
  IconInfo,
  IconPlus,
  IconTrash,
  Input,
  NumberInput,
  Select,
  Skeleton,
  Switch,
  Textarea,
  TreeView,
  Tooltip,
  useToast,
  type TreeNode,
} from '@/design-system';
import { documentTypesApi, queryKeys, toUserMessage } from '@/lib/api';
import type { DocumentTypeNode } from '@/lib/api/types';
import {
  APPROVAL_POLICY_DESCRIPTION,
  APPROVAL_POLICY_LABEL,
  NOTICE_FREQUENCY_LABEL,
} from '@/lib/i18n';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * CRUD de tipos de documento.
 *
 * A hierarquia é por IDENTIFICADOR (`parentId`), nunca por nome — o legado
 * casava as duas árvores pelo NOME do tipo (ged.md §10.2, achado C14), o que
 * quebrava com homônimos em ramos diferentes e impedia renomear um tipo sem
 * órfãos.
 *
 * REORDENAÇÃO: em vez de arrastar-e-soltar puro, os botões "mover para cima" e
 * "mover para baixo" são o caminho PRIMÁRIO. Arrastar continua disponível para
 * quem usa mouse, mas WCAG 2.5.7 exige que qualquer operação de arrasto tenha
 * alternativa de ponteiro único — e teclado. Aqui a alternativa é acionável por
 * Tab + Enter e anuncia a nova posição.
 */
export function DocumentTypesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<DocumentTypeNode | null>(null);

  const treeQuery = useQuery({
    queryKey: queryKeys.documentTypes.tree(),
    queryFn: () => documentTypesApi.tree(),
  });

  // `?? []` cria array novo a cada render; sem o memo, `flat` recalculava sempre.
  const tree = useMemo(() => treeQuery.data ?? [], [treeQuery.data]);
  const flat = useMemo(() => flatten(tree), [tree]);
  const selected = useMemo(
    () => flat.find((node) => node.id === selectedId) ?? null,
    [flat, selectedId],
  );

  const remove = useMutation({
    mutationFn: (id: string) => documentTypesApi.remove(id),
    onSuccess: async () => {
      setDeleting(null);
      setSelectedId(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.documentTypes.all() });
      toast({ tone: 'success', title: 'Tipo excluído' });
    },
    onError: (error) => {
      setDeleting(null);
      toast({
        tone: 'error',
        title: 'Não foi possível excluir',
        description: toUserMessage(error),
      });
    },
  });

  const reorder = useMutation({
    mutationFn: (params: { node: DocumentTypeNode; sortOrder: number }) =>
      documentTypesApi.update(params.node.id, {
        name: params.node.name,
        description: params.node.description,
        parentId: params.node.parentId,
        noticeFrequency: params.node.noticeFrequency,
        defaultValidityMonths: params.node.defaultValidityMonths,
        defaultApprovalPolicy: params.node.defaultApprovalPolicy,
        defaultQuorumSize: params.node.defaultQuorumSize,
        sortOrder: params.sortOrder,
        active: params.node.active,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.documentTypes.all() });
    },
  });

  const siblings = useMemo(
    () =>
      selected
        ? flat
            .filter((node) => node.parentId === selected.parentId)
            .sort((a, b) => a.sortOrder - b.sortOrder)
        : [],
    [flat, selected],
  );

  const moveSelected = (direction: -1 | 1) => {
    if (!selected) return;
    const index = siblings.findIndex((node) => node.id === selected.id);
    const target = siblings[index + direction];
    if (!target) return;
    // Troca de posição entre irmãos: duas atualizações, uma para cada.
    reorder.mutate({ node: selected, sortOrder: target.sortOrder });
    reorder.mutate({ node: target, sortOrder: selected.sortOrder });
    toast({
      tone: 'info',
      title: 'Ordem atualizada',
      description: `"${selected.name}" agora aparece ${
        direction === -1 ? 'antes' : 'depois'
      } de "${target.name}".`,
    });
  };

  return (
    <>
      <PageHeader
        title="Tipos de documento"
        description="Hierarquia usada para classificar os documentos controlados. Cada tipo define padrões de validade, política de aprovação e frequência de aviso de vencimento."
        actions={
          <Button
            variant="primary"
            leadingIcon={<IconPlus size={15} />}
            onClick={() => {
              setCreating(true);
              setSelectedId(null);
            }}
          >
            Novo tipo
          </Button>
        }
      />

      <PageBody>
        <div className="grid gap-5 lg:grid-cols-[20rem_1fr]">
          <Card padding="sm" className="lg:sticky lg:top-20 lg:self-start">
            <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-content-muted">
              Hierarquia
            </h2>

            {treeQuery.isPending ? (
              <div className="flex flex-col gap-1.5 p-1">
                {Array.from({ length: 8 }, (_, index) => (
                  <Skeleton key={index} className="h-7 w-full" />
                ))}
              </div>
            ) : tree.length === 0 ? (
              <EmptyState
                variant="compact"
                icon={<IconFolder size={20} />}
                title="Nenhum tipo cadastrado"
                description="Crie o primeiro tipo para começar a classificar documentos."
              />
            ) : (
              <>
                <TreeView
                  ariaLabel="Tipos de documento"
                  nodes={toTreeNodes(tree)}
                  selectedId={selectedId}
                  onSelect={(node) => {
                    setCreating(false);
                    setSelectedId(node?.id ?? null);
                  }}
                  renderNodeSuffix={(node) =>
                    (node.meta as DocumentTypeNode | undefined)?.active === false ? (
                      <Badge tone="neutral" size="sm">
                        Inativo
                      </Badge>
                    ) : null
                  }
                />

                {selected ? (
                  <div className="mt-3 flex items-center gap-1 border-t border-border-subtle pt-3">
                    <span className="mr-auto flex items-center gap-1 text-xs text-content-subtle">
                      <IconGrip size={13} aria-hidden="true" />
                      Reordenar
                    </span>
                    {/* Alternativa por teclado ao arrasto — WCAG 2.5.7. */}
                    <Button
                      size="icon"
                      variant="ghost"
                      iconLabel={`Mover "${selected.name}" para cima`}
                      disabled={siblings[0]?.id === selected.id || reorder.isPending}
                      onClick={() => moveSelected(-1)}
                    >
                      <IconChevronUp size={16} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      iconLabel={`Mover "${selected.name}" para baixo`}
                      disabled={
                        siblings[siblings.length - 1]?.id === selected.id || reorder.isPending
                      }
                      onClick={() => moveSelected(1)}
                    >
                      <IconChevronDown size={16} />
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </Card>

          <div className="min-w-0">
            {creating ? (
              <TypeForm
                key="create"
                mode="create"
                types={flat}
                onDone={(id) => {
                  setCreating(false);
                  setSelectedId(id);
                }}
                onCancel={() => setCreating(false)}
              />
            ) : selected ? (
              <TypeForm
                key={selected.id}
                mode="edit"
                node={selected}
                types={flat}
                onDone={() => undefined}
                onDelete={() => setDeleting(selected)}
              />
            ) : (
              <Card>
                <EmptyState
                  icon={<IconFolder size={22} />}
                  title="Selecione um tipo"
                  description="Escolha um tipo na hierarquia ao lado para ver e editar suas configurações, ou crie um novo tipo."
                />
              </Card>
            )}
          </div>
        </div>
      </PageBody>

      {deleting ? (
        <AlertDialog
          open
          onOpenChange={(open) => {
            if (!open) setDeleting(null);
          }}
          title={`Excluir o tipo "${deleting.name}"?`}
          description="Esta ação não pode ser desfeita. O tipo só pode ser excluído se não houver documentos nem subtipos vinculados a ele."
          confirmLabel="Excluir tipo"
          loading={remove.isPending}
          onConfirm={() => remove.mutate(deleting.id)}
        >
          {deleting.documentCount > 0 || deleting.children.length > 0 ? (
            <p className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2 text-sm text-warning-content">
              <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
              <span>
                Este tipo tem {deleting.documentCount} documento(s) e {deleting.children.length}{' '}
                subtipo(s). O servidor recusará a exclusão. Mova ou reclassifique os vínculos antes.
              </span>
            </p>
          ) : null}
        </AlertDialog>
      ) : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Formulário do tipo
// ─────────────────────────────────────────────────────────────────────────────

function TypeForm({
  mode,
  node,
  types,
  onDone,
  onCancel,
  onDelete,
}: {
  readonly mode: 'create' | 'edit';
  readonly node?: DocumentTypeNode;
  readonly types: readonly DocumentTypeNode[];
  readonly onDone: (id: string) => void;
  readonly onCancel?: () => void;
  readonly onDelete?: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const form = useForm<DocumentTypeInput>({
    resolver: zodResolver(documentTypeSchema),
    defaultValues: node
      ? {
          name: node.name,
          description: node.description,
          parentId: node.parentId,
          noticeFrequency: node.noticeFrequency,
          defaultValidityMonths: node.defaultValidityMonths,
          defaultApprovalPolicy: node.defaultApprovalPolicy,
          defaultQuorumSize: node.defaultQuorumSize,
          sortOrder: node.sortOrder,
          active: node.active,
        }
      : {
          name: '',
          description: '',
          parentId: null,
          noticeFrequency: 'NONE',
          defaultValidityMonths: 0,
          defaultApprovalPolicy: ApprovalPolicy.ANY_ONE,
          defaultQuorumSize: null,
          sortOrder: 0,
          active: true,
        },
  });

  useEffect(() => {
    if (node) form.reset({ ...node });
  }, [node, form]);

  const save = useMutation({
    mutationFn: (values: DocumentTypeInput) =>
      mode === 'create'
        ? documentTypesApi.create(values)
        : documentTypesApi.update(node?.id as string, values),
    onError: (error) => form.setError('root', { message: toUserMessage(error) }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.documentTypes.all() });
      toast({
        tone: 'success',
        title: mode === 'create' ? 'Tipo criado' : 'Tipo atualizado',
      });
      onDone('id' in result ? result.id : (node?.id ?? ''));
    },
  });

  const policy = form.watch('defaultApprovalPolicy');
  const parentOptions = types.filter((candidate) => candidate.id !== node?.id);

  return (
    <Card>
      <CardHeader
        headingLevel={2}
        title={mode === 'create' ? 'Novo tipo de documento' : `Editar "${node?.name}"`}
        description="Os valores definidos aqui são sugeridos como padrão quando um documento deste tipo é criado."
        action={
          mode === 'edit' && onDelete ? (
            <Button variant="danger-outline" leadingIcon={<IconTrash size={15} />} onClick={onDelete}>
              Excluir
            </Button>
          ) : undefined
        }
      />

      <form
        className="mt-5 grid gap-5 lg:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit((values) => save.mutate(values))();
        }}
        noValidate
      >
        {form.formState.errors.root ? (
          <FormError className="lg:col-span-2">{form.formState.errors.root.message}</FormError>
        ) : null}

        <div className="flex flex-col gap-4">
          <Field label="Nome" required error={form.formState.errors.name?.message}>
            <Input {...form.register('name')} maxLength={255} />
          </Field>

          <Controller
            control={form.control}
            name="parentId"
            render={({ field }) => (
              <Field
                label="Tipo pai"
                description="Deixe em branco para criar um tipo de primeiro nível. Um tipo não pode ser movido para dentro de um descendente dele mesmo."
              >
                <Select
                  value={field.value ?? '__root__'}
                  onValueChange={(value) => field.onChange(value === '__root__' ? null : value)}
                  options={[
                    { value: '__root__', label: 'Sem tipo pai (primeiro nível)' },
                    ...parentOptions.map((candidate) => ({
                      value: candidate.id,
                      label: candidate.path || candidate.name,
                    })),
                  ]}
                />
              </Field>
            )}
          />

          <Field label="Descrição" error={form.formState.errors.description?.message}>
            <Textarea {...form.register('description')} rows={3} maxLength={4000} />
          </Field>

          <Controller
            control={form.control}
            name="active"
            render={({ field }) => (
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                stateLabels={{ on: 'Ativo', off: 'Inativo' }}
                label="Tipo ativo"
                description="Tipos inativos não podem ser escolhidos em novos documentos, mas continuam classificando os documentos existentes."
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-4">
          <Controller
            control={form.control}
            name="defaultApprovalPolicy"
            render={({ field }) => (
              <Field label="Política de aprovação padrão" required>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={Object.values(ApprovalPolicy).map((value) => ({
                    value,
                    label: APPROVAL_POLICY_LABEL[value],
                    description: APPROVAL_POLICY_DESCRIPTION[value],
                  }))}
                />
              </Field>
            )}
          />

          {policy === ApprovalPolicy.QUORUM ? (
            <Field
              label="Quórum padrão"
              error={form.formState.errors.defaultQuorumSize?.message}
            >
              <NumberInput {...form.register('defaultQuorumSize')} min={1} max={50} className="w-24" />
            </Field>
          ) : null}

          <Field
            label="Validade padrão (meses)"
            error={form.formState.errors.defaultValidityMonths?.message}
            description="Zero significa sem prazo de validade."
          >
            <NumberInput {...form.register('defaultValidityMonths')} min={0} max={600} className="w-28" />
          </Field>

          <Controller
            control={form.control}
            name="noticeFrequency"
            render={({ field }) => (
              <Field
                label="Frequência de aviso de vencimento"
                description="Com que periodicidade os responsáveis são notificados sobre documentos deste tipo próximos do vencimento."
                labelAddon={
                  <Tooltip content="Define com que antecedência o sistema avisa sobre documentos deste tipo perto de vencer.">
                    <span className="cursor-help text-content-subtle">
                      <IconInfo size={14} aria-hidden="true" />
                      <span className="qc-sr-only">
                        O que a frequência de aviso controla
                      </span>
                    </span>
                  </Tooltip>
                }
              >
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={(['NONE', 'DAILY', 'WEEKLY', 'MONTHLY'] as const).map((value) => ({
                    value,
                    label: NOTICE_FREQUENCY_LABEL[value],
                  }))}
                />
              </Field>
            )}
          />

          <Field
            label="Ordem de exibição"
            error={form.formState.errors.sortOrder?.message}
            description="Número menor aparece primeiro entre os tipos do mesmo nível."
          >
            <NumberInput {...form.register('sortOrder')} min={0} className="w-24" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 lg:col-span-2">
          {onCancel ? (
            <Button variant="secondary" onClick={onCancel}>
              Cancelar
            </Button>
          ) : null}
          <Button type="submit" variant="primary" loading={save.isPending}>
            {mode === 'create' ? 'Criar tipo' : 'Salvar alterações'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function toTreeNodes(nodes: readonly DocumentTypeNode[]): TreeNode[] {
  return nodes.map((node) => ({
    id: node.id,
    label: node.name,
    count: node.documentCount,
    children: node.children.length > 0 ? toTreeNodes(node.children) : undefined,
    meta: node,
  }));
}

function flatten(nodes: readonly DocumentTypeNode[]): DocumentTypeNode[] {
  return nodes.flatMap((node) => [node, ...flatten(node.children)]);
}
